import { Component, inject, signal } from '@angular/core';
import { Observable, take, tap } from 'rxjs';
import { AppState } from 'src/app/app.state';
import { Organization } from 'src/app/features/organization/organization-models';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { PriceRegulationListStateService } from '../services/price-regulation-list.state.service';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdminEnumSelectListComponent } from 'src/app/shared/components/admin-enum-select-list/admin-enum-select-list.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';
import { PriceRegulationType } from '../price-regulation';
import { TranslatePriceRegulationTypePipe } from 'src/app/shared/pipes/translate-price-regulation-type.pipe';
import { RequiresFeatureRoleDirective } from 'src/app/shared/directives/requires-feature-role.directive';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { PriceRegulationsListComponent } from '../price-regulations-list/price-regulations-list.component';
import { MatDialog } from '@angular/material/dialog';
import {
  PriceRegulationDialogCloseResult,
  PriceRegulationsCreateEditModalComponent,
} from '../price-regulations-create-edit-modal/price-regulations-create-edit-modal.component';
import { ActionLogModalComponent } from 'src/app/shared/components/modals/actionlog-modal/action-log-modal/action-log-modal.component';
import { EntityType } from '../../action-log';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'adm-price-regulations-overview',
  templateUrl: './price-regulations-overview.component.html',
  styleUrls: ['./price-regulations-overview.component.scss'],
  imports: [
    TextSearchComponent,
    AdmOrgTypeaheadComponent,
    AdminEnumSelectListComponent,
    MatSlideToggle,
    MatButton,
    MatIcon,
    RequiresFeatureRoleDirective,
    PriceRegulationsListComponent,
  ],
  providers: [PriceRegulationListStateService],
})
export class PriceRegulationsOverviewComponent {
  appState = inject(AppState);
  environmentLoaderService = inject(EnvironmentLoaderService);
  private dialog = inject(MatDialog);
  public state = inject(PriceRegulationListStateService);

  public roles = this.environmentLoaderService.virtualRoles;
  public Permission = Permission;
  public typeOptions = Object.values(PriceRegulationType);
  public translatePriceRegulationTypePipe = new TranslatePriceRegulationTypePipe();
  public fetchedOrganizations = signal<Organization[] | null>(null);

  organizationsFetched(orgnizations: Organization[] | null) {
    this.fetchedOrganizations.set(orgnizations);
  }

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.filters.update(x => ({
        ...x,
        query: value && value.length > 0 ? value : null,
        page: { ...x.page, pageIndex: 0 },
      }));
    });
  };

  organizationSelected = (org?: Organization | null) => {
    this.state.filters.update(x => ({
      ...x,
      organizationId: org ? org.id : null,
      onlyGlobal: org ? false : x.onlyGlobal,
      page: { ...x.page, pageIndex: 0 },
    }));
  };

  typeSelected(type: PriceRegulationType | null) {
    this.state.filters.update(x => ({
      ...x,
      type: type,
      page: { ...x.page, pageIndex: 0 },
    }));
  }

  toggleGlobalOnly(checked: boolean) {
    this.state.filters.update(x => ({
      ...x,
      onlyGlobal: checked,
      organizationId: checked ? null : x.organizationId,
      page: { ...x.page, pageIndex: 0 },
    }));

    if (checked) {
      this.state.resetAllFiltersTrigger.set({ reset: true });
    }
  }

  openActionLog() {
    this.dialog.open(ActionLogModalComponent, {
      data: { organizations: this.fetchedOrganizations(), entityType: EntityType.PriceRegulation },
      disableClose: true,
      width: '1024px',
    });
  }

  openCreatePriceRegulation() {
    const dialogRef = this.dialog.open(PriceRegulationsCreateEditModalComponent, {
      disableClose: true,
      width: '650px',
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        tap((result: PriceRegulationDialogCloseResult | undefined) => {
          if (!result?.saved) {
            return;
          }

          if (result.priceRegulation) {
            this.state.filterToPriceRegulation(result.priceRegulation);
          }
        })
      )
      .subscribe();
  }
}
